'use client';

import { c as _c } from "react/compiler-runtime";
import type { IndustryProfile } from '@lib/onboarding/constants/industry-profiles';
import type { GoalId } from '@lib/onboarding/constants/setup-options';
import { cn } from 'cn';
import { BarChart3, Check, CreditCard, FileSignature, FileText, FolderOpen, Inbox, MessageSquare, PenLine, Send, ShieldCheck, Sparkles, Table2 } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Line, Pill, PreviewWindow } from './preview-frame';
import { useTicker } from './use-ticker';
interface GoalPreviewProps {
  accent: string;
  name: string;
  /** Supplies industry-specific document and deal names. */
  profile: IndustryProfile;
}

/** "Listing agreement" → "listing agreements"; acronyms keep their case. */
function pluralDoc(name: string): string {
  const lower = /^[A-Z0-9]+$/.test(name) ? name : name.toLowerCase();
  return lower.endsWith('s') ? lower : `${lower}s`;
}
const RESIDENTIAL_CHECKLIST = ['Agency disclosure signed', 'Lead paint addendum', 'Earnest money receipt', 'Final walkthrough form'];
function ReplaceLegacyPreview(t0) {
  const $ = _c(15);
  const {
    accent
  } = t0;
  const tick = useTicker(2, 2200);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      icon: FolderOpen,
      label: "Shared drive"
    }, {
      icon: Table2,
      label: "Spreadsheets"
    }, {
      icon: Inbox,
      label: "Email threads"
    }, {
      icon: FileSignature,
      label: "Old e-sign app"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const tools = t1;
  let t2;
  if ($[1] !== tick) {
    t2 = tools.map((tool, i) => {
      const angle = i / tools.length * Math.PI * 2 - Math.PI / 4;
      const collapsed = tick === 1;
      return <motion.div key={tool.label} className="absolute flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 shadow-sm" animate={{
        x: collapsed ? 0 : Math.cos(angle) * 120,
        y: collapsed ? 0 : Math.sin(angle) * 70,
        opacity: collapsed ? 0 : 1,
        scale: collapsed ? 0.6 : 1
      }} transition={{
        type: "spring",
        stiffness: 120,
        damping: 16
      }}><tool.icon className="h-4 w-4 text-gray-400" />{tool.label}</motion.div>;
    });
    $[1] = tick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== accent) {
    t3 = {
      backgroundColor: accent
    };
    $[3] = accent;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = tick === 1 ? 1.15 : 0.9;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      scale: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      type: "spring",
      stiffness: 200,
      damping: 12
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Sparkles className="h-4 w-4" data-sentry-element="Sparkles" data-sentry-source-file="goal-previews.tsx" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t3 || $[10] !== t5) {
    t8 = <motion.div className="flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-lg" style={t3} animate={t5} transition={t6} data-sentry-element="motion.div" data-sentry-source-file="goal-previews.tsx">{t7} Clai</motion.div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t2 || $[13] !== t8) {
    t9 = <PreviewWindow title="Everything in one place" data-sentry-element="PreviewWindow" data-sentry-component="ReplaceLegacyPreview" data-sentry-source-file="goal-previews.tsx"><div className="relative flex h-56 items-center justify-center">{t2}{t8}</div></PreviewWindow>;
    $[12] = t2;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
function CollectSignaturesPreview(t0) {
  const $ = _c(27);
  const {
    accent,
    profile
  } = t0;
  const tick = useTicker(5, 1000);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== profile.sampleTemplates[0] || $[1] !== tick) {
    const signers = ["Jordan Lee", "Priya Patel", "Sam Rivera"];
    T0 = PreviewWindow;
    t3 = `${profile.sampleTemplates[0]} · 3 signers`;
    t4 = "PreviewWindow";
    t5 = "CollectSignaturesPreview";
    t6 = "goal-previews.tsx";
    t1 = "space-y-3";
    t2 = signers.map((signer, i) => {
      const signed = tick > i + 1;
      const viewed = tick > i;
      return <motion.div key={signer} className="flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2.5" initial={{
        opacity: 0,
        x: -8
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: i * 0.1
      }}><div className="flex items-center gap-2"><div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-[11px] font-medium text-gray-600">{signer.split(" ").map(_temp).join("")}</div><span className="text-sm text-gray-800">{signer}</span></div><AnimatePresence mode="wait"><motion.span key={signed ? "signed" : viewed ? "viewed" : "sent"} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }}>{signed ? <Pill tone="green"><Check className="h-3 w-3" /> Signed</Pill> : viewed ? <Pill tone="blue">Viewed</Pill> : <Pill>Sent</Pill>}</motion.span></AnimatePresence></motion.div>;
    });
    $[0] = profile.sampleTemplates[0];
    $[1] = tick;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== accent) {
    t7 = {
      backgroundColor: accent
    };
    $[9] = accent;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = `${Math.min(100, Math.max(0, tick - 1) * 34)}%`;
  let t9;
  if ($[11] !== t8) {
    t9 = {
      width: t8
    };
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t7 || $[14] !== t9) {
    t10 = <div className="h-2 overflow-hidden rounded-full bg-gray-100"><motion.div className="h-full rounded-full" style={t7} animate={t9} data-sentry-element="motion.div" data-sentry-source-file="goal-previews.tsx" /></div>;
    $[13] = t7;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t1 || $[17] !== t10 || $[18] !== t2) {
    t11 = <div className={t1}>{t2}{t10}</div>;
    $[16] = t1;
    $[17] = t10;
    $[18] = t2;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== T0 || $[21] !== t11 || $[22] !== t3 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
    t12 = <T0 title={t3} data-sentry-element={t4} data-sentry-component={t5} data-sentry-source-file={t6}>{t11}</T0>;
    $[20] = T0;
    $[21] = t11;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
function _temp(w) {
  return w[0];
}
function SignDocumentsPreview(t0) {
  const $ = _c(22);
  const {
    accent,
    profile
  } = t0;
  const tick = useTicker(2, 2400);
  const t1 = `${profile.sampleTemplates[1] ?? profile.sampleTemplates[0]}.pdf`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="space-y-2"><Line width="70%" data-sentry-element="Line" data-sentry-source-file="goal-previews.tsx" /><Line width="95%" delay={0.05} data-sentry-element="Line" data-sentry-source-file="goal-previews.tsx" /><Line width="88%" delay={0.1} data-sentry-element="Line" data-sentry-source-file="goal-previews.tsx" /><Line width="60%" delay={0.15} data-sentry-element="Line" data-sentry-source-file="goal-previews.tsx" /></div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== accent) {
    t3 = {
      borderColor: accent
    };
    $[1] = accent;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-[10px] font-medium tracking-wide text-gray-400 uppercase">Sign here</p>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  let t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      pathLength: 0
    };
    t6 = {
      pathLength: 1
    };
    t7 = {
      duration: 1.6,
      ease: "easeInOut"
    };
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
  }
  let t8;
  if ($[7] !== tick) {
    t8 = <svg viewBox="0 0 220 60" className="h-14 w-full" aria-hidden={true} data-sentry-element="svg" data-sentry-source-file="goal-previews.tsx"><motion.path key={tick} d="M8 44 C 24 10, 34 10, 38 40 S 52 60, 60 30 S 74 8, 82 36 S 96 52, 104 30 C 110 18, 118 20, 120 36 C 124 50, 136 44, 146 28 S 170 22, 176 40 S 200 34, 212 22" fill="none" stroke="#111827" strokeWidth="2.5" strokeLinecap="round" initial={t5} animate={t6} transition={t7} data-sentry-element="motion.path" data-sentry-source-file="goal-previews.tsx" /></svg>;
    $[7] = tick;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t8) {
    t9 = <div className="mt-6 rounded-xl border-2 border-dashed p-3" style={t3}>{t4}{t8}</div>;
    $[9] = t3;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== accent) {
    t10 = {
      backgroundColor: accent
    };
    $[12] = accent;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      scale: [1, 1.03, 1]
    };
    t12 = {
      delay: 1.6,
      duration: 0.4
    };
    $[14] = t11;
    $[15] = t12;
  } else {
    t11 = $[14];
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t10) {
    t13 = <motion.button type="button" tabIndex={-1} className="mt-4 w-full rounded-xl py-2 text-sm font-medium text-white" style={t10} animate={t11} transition={t12} data-sentry-element="motion.button" data-sentry-source-file="goal-previews.tsx">Finish signing</motion.button>;
    $[16] = t10;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t1 || $[19] !== t13 || $[20] !== t9) {
    t14 = <PreviewWindow title={t1} data-sentry-element="PreviewWindow" data-sentry-component="SignDocumentsPreview" data-sentry-source-file="goal-previews.tsx">{t2}{t9}{t13}</PreviewWindow>;
    $[18] = t1;
    $[19] = t13;
    $[20] = t9;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  return t14;
}
function WebFormsPreview(t0) {
  const $ = _c(9);
  const {
    accent
  } = t0;
  const tick = useTicker(5, 900);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["Full name", "Email", "Property address", "Move-in date"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const fields = t1;
  let t2;
  if ($[1] !== tick) {
    t2 = <div className="space-y-2.5">{fields.map((field, i) => <AnimatePresence key={field}>{tick >= i ? <motion.div initial={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} className="rounded-lg border border-gray-200 px-3 py-2"><p className="text-[10px] font-medium text-gray-400">{field}</p><Line width={`${40 + i * 17 % 40}%`} className="mt-1.5" /></motion.div> : null}</AnimatePresence>)}</div>;
    $[1] = tick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="truncate font-mono text-[11px] text-gray-500">clai.link/apply</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== accent) {
    t4 = <div className="mt-4 flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2">{t3}<span className="rounded-lg px-2.5 py-1 text-xs font-medium text-white" style={{
        backgroundColor: accent
      }}>Share link</span></div>;
    $[4] = accent;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <PreviewWindow title={"Rental application \xB7 form"} data-sentry-element="PreviewWindow" data-sentry-component="WebFormsPreview" data-sentry-source-file="goal-previews.tsx">{t2}{t4}</PreviewWindow>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function AutomateContractsPreview(t0) {
  const $ = _c(22);
  const {
    accent
  } = t0;
  const tick = useTicker(2, 1800);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== accent || $[1] !== tick) {
    const fields = [{
      token: "{{Buyer}}",
      value: "Jordan Lee"
    }, {
      token: "{{Price}}",
      value: "$1,250,000"
    }, {
      token: "{{Closing}}",
      value: "Nov 14, 2026"
    }];
    T0 = PreviewWindow;
    t3 = "Your template \u2192 ready to send";
    t4 = "PreviewWindow";
    t5 = "AutomateContractsPreview";
    t6 = "goal-previews.tsx";
    t1 = "space-y-3 text-sm leading-7 text-gray-700";
    t2 = fields.map(field => <p key={field.token}><span className="text-gray-400">{field.token.replace(/[{}]/g, "")}:{" "}</span><AnimatePresence mode="wait"><motion.span key={tick === 1 ? field.value : field.token} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} className={cn("rounded-md px-1.5 py-0.5 font-medium", tick === 1 ? "text-gray-900" : "font-mono text-xs")} style={{
          backgroundColor: tick === 1 ? `${accent}22` : "#f3f4f6"
        }}>{tick === 1 ? field.value : field.token}</motion.span></AnimatePresence></p>);
    $[0] = accent;
    $[1] = tick;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== accent) {
    t8 = <div className="mt-4 flex items-center gap-2 text-xs text-gray-500"><Sparkles className="h-3.5 w-3.5" style={{
        color: accent
      }} data-sentry-element="Sparkles" data-sentry-source-file="goal-previews.tsx" />Auto-filled from the deal in one click</div>;
    $[12] = accent;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== T0 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <T0 title={t3} data-sentry-element={t4} data-sentry-component={t5} data-sentry-source-file={t6}>{t7}{t8}</T0>;
    $[14] = T0;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function ContractInsightsPreview(t0) {
  const $ = _c(15);
  const {
    accent,
    profile
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [38, 62, 45, 80, 56, 92];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const bars = t1;
  let t2;
  if ($[1] !== accent) {
    t2 = <div className="flex h-28 items-end gap-2">{bars.map((height, i) => <motion.div key={i} className="flex-1 rounded-t-md" style={{
        backgroundColor: i === bars.length - 1 ? accent : `${accent}55`
      }} initial={{
        height: 0
      }} animate={{
        height: `${height}%`
      }} transition={{
        delay: i * 0.08,
        type: "spring",
        stiffness: 90
      }} />)}</div>;
    $[1] = accent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mt-3 flex flex-wrap gap-1.5"><Pill tone="amber" data-sentry-element="Pill" data-sentry-source-file="goal-previews.tsx">12 renewals due in 60 days</Pill><Pill tone="blue" data-sentry-element="Pill" data-sentry-source-file="goal-previews.tsx">Avg. close: 34 days</Pill></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 8
    };
    t5 = {
      opacity: 1,
      y: 0
    };
    t6 = {
      delay: 0.8
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MessageSquare className="mt-0.5 h-4 w-4 text-gray-400" data-sentry-element="MessageSquare" data-sentry-source-file="goal-previews.tsx" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== profile.sampleTemplates[0]) {
    t8 = pluralDoc(profile.sampleTemplates[0]);
    $[8] = profile.sampleTemplates[0];
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <motion.div initial={t4} animate={t5} transition={t6} className="mt-4 flex items-start gap-2 rounded-xl bg-gray-50 p-3" data-sentry-element="motion.div" data-sentry-source-file="goal-previews.tsx">{t7}<p className="text-xs text-gray-600">“Which {t8} expire this quarter?”</p></motion.div>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t2 || $[13] !== t9) {
    t10 = <PreviewWindow title={"Insights \xB7 past contracts"} data-sentry-element="PreviewWindow" data-sentry-component="ContractInsightsPreview" data-sentry-source-file="goal-previews.tsx">{t2}{t3}{t9}</PreviewWindow>;
    $[12] = t2;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}
function CreateContractsPreview(t0) {
  const $ = _c(25);
  const {
    accent,
    profile
  } = t0;
  const docName = profile.sampleTemplates[0];
  let noun;
  let t1;
  if ($[0] !== docName) {
    noun = pluralDoc(docName).replace(/s$/, "");
    t1 = /^[aeiou]/i.test(noun) ? "an" : "a";
    $[0] = docName;
    $[1] = noun;
    $[2] = t1;
  } else {
    noun = $[1];
    t1 = $[2];
  }
  const prompt = `Draft ${t1} ${noun} with Clai AI…`;
  const tick = useTicker(prompt.length + 12, 70);
  let t2;
  if ($[3] !== prompt || $[4] !== tick) {
    t2 = prompt.slice(0, Math.min(tick, prompt.length));
    $[3] = prompt;
    $[4] = tick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const typed = t2;
  const drafting = tick > prompt.length;
  let t3;
  if ($[6] !== accent) {
    t3 = <Sparkles className="h-4 w-4 shrink-0" style={{
      color: accent
    }} data-sentry-element="Sparkles" data-sentry-source-file="goal-previews.tsx" />;
    $[6] = accent;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="ml-px inline-block h-4 w-px animate-pulse bg-gray-400 align-middle" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== typed) {
    t5 = <span className="truncate text-sm text-gray-800">{typed}{t4}</span>;
    $[9] = typed;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = <div className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5">{t3}{t5}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = drafting ? docName : " ";
  let t8;
  if ($[14] !== t7) {
    t8 = <p className="text-sm font-semibold text-gray-900">{t7}</p>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [92, 84, 96, 70, 88, 60];
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== drafting) {
    t10 = t9.map((w, i) => <motion.div key={i} className="h-2 rounded-full bg-gray-200" animate={{
      width: drafting ? `${w}%` : "0%",
      opacity: drafting ? 1 : 0
    }} transition={{
      delay: drafting ? i * 0.08 : 0
    }} />);
    $[17] = drafting;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t8) {
    t11 = <div className="mt-4 space-y-2">{t8}{t10}</div>;
    $[19] = t10;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t6) {
    t12 = <PreviewWindow title={"Clai AI \xB7 new contract"} data-sentry-element="PreviewWindow" data-sentry-component="CreateContractsPreview" data-sentry-source-file="goal-previews.tsx">{t6}{t11}</PreviewWindow>;
    $[22] = t11;
    $[23] = t6;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function CompliancePreview(t0) {
  const $ = _c(33);
  const {
    accent,
    profile
  } = t0;
  const tick = useTicker(6, 800);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== accent || $[1] !== profile.id || $[2] !== profile.sampleDeal || $[3] !== profile.sampleTemplates || $[4] !== tick) {
    const items = profile.id === "residential_real_estate" ? RESIDENTIAL_CHECKLIST : profile.sampleTemplates.slice(0, 4).map(_temp2);
    T0 = PreviewWindow;
    t3 = `Deal compliance · ${profile.sampleDeal}`;
    t4 = "PreviewWindow";
    t5 = "CompliancePreview";
    t6 = "goal-previews.tsx";
    t1 = "space-y-2";
    let t7;
    if ($[12] !== accent || $[13] !== tick) {
      t7 = (item, i) => {
        const done = tick > i;
        return <div key={item} className="flex items-center gap-3 py-1"><motion.span className="flex h-5 w-5 items-center justify-center rounded-full border" animate={{
            backgroundColor: done ? accent : "#ffffff",
            borderColor: done ? accent : "#d1d5db"
          }}>{done ? <Check className="h-3 w-3 text-white" /> : null}</motion.span><span className={cn("text-sm", done ? "text-gray-900" : "text-gray-500")}>{item}</span></div>;
      };
      $[12] = accent;
      $[13] = tick;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t2 = items.map(t7);
    $[0] = accent;
    $[1] = profile.id;
    $[2] = profile.sampleDeal;
    $[3] = profile.sampleTemplates;
    $[4] = tick;
    $[5] = T0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    T0 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[15] !== t1 || $[16] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = tick >= 4 ? "#dcfce7" : "#f3f4f6";
  const t9 = tick >= 4 ? "#15803d" : "#6b7280";
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = {
      backgroundColor: t8,
      color: t9
    };
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ShieldCheck className="h-4 w-4" data-sentry-element="ShieldCheck" data-sentry-source-file="goal-previews.tsx" />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = tick >= 4 ? "File complete \xB7 audit trail saved" : "Checking file\u2026";
  let t13;
  if ($[22] !== t10 || $[23] !== t12) {
    t13 = <motion.div className="mt-4 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium" animate={t10} data-sentry-element="motion.div" data-sentry-source-file="goal-previews.tsx">{t11}{t12}</motion.div>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== T0 || $[26] !== t13 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t14 = <T0 title={t3} data-sentry-element={t4} data-sentry-component={t5} data-sentry-source-file={t6}>{t7}{t13}</T0>;
    $[25] = T0;
    $[26] = t13;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
function _temp2(t) {
  return `${t} signed`;
}
function PaymentsPreview(t0) {
  const $ = _c(9);
  const {
    accent,
    profile
  } = t0;
  const tick = useTicker(3, 1500);
  const t1 = `Invoice · ${profile.sampleDeal}`;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-xs text-gray-500">Amount due</p>;
    t3 = <p className="font-ivar text-3xl text-gray-900">$2,500.00</p>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-4 space-y-2"><Line width="60%" data-sentry-element="Line" data-sentry-source-file="goal-previews.tsx" /><Line width="40%" delay={0.1} data-sentry-element="Line" data-sentry-source-file="goal-previews.tsx" /></div>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== accent || $[4] !== tick) {
    t5 = <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-source-file="goal-previews.tsx">{tick < 2 ? <motion.div key="pay" initial={{
        opacity: 0
      }} animate={{
        opacity: 1,
        scale: tick === 1 ? 0.97 : 1
      }} exit={{
        opacity: 0
      }} className="mt-5 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium text-white" style={{
        backgroundColor: accent
      }}><CreditCard className="h-4 w-4" />{tick === 1 ? "Processing\u2026" : "Pay now"}</motion.div> : <motion.div key="paid" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-green-50 py-2.5 text-sm font-medium text-green-700"><Check className="h-4 w-4" /> Paid · deposited to your account</motion.div>}</AnimatePresence>;
    $[3] = accent;
    $[4] = tick;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t1 || $[7] !== t5) {
    t6 = <PreviewWindow title={t1} data-sentry-element="PreviewWindow" data-sentry-component="PaymentsPreview" data-sentry-source-file="goal-previews.tsx">{t2}{t3}{t4}{t5}</PreviewWindow>;
    $[6] = t1;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function ConversationToClosePreview(t0) {
  const $ = _c(16);
  const {
    accent
  } = t0;
  const tick = useTicker(5, 1100);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      icon: MessageSquare,
      label: "Chat"
    }, {
      icon: FileText,
      label: "Contract"
    }, {
      icon: PenLine,
      label: "Signed"
    }, {
      icon: CreditCard,
      label: "Paid"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const stages = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <motion.div initial={{
      opacity: 0,
      x: -10
    }} animate={{
      opacity: 1,
      x: 0
    }} className="w-3/4 rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-xs text-gray-700" data-sentry-element="motion.div" data-sentry-source-file="goal-previews.tsx">We'd love to move forward at $1.2M 🙌</motion.div>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0,
      x: 10
    };
    t4 = {
      opacity: 1,
      x: 0
    };
    t5 = {
      delay: 0.4
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== accent) {
    t6 = {
      backgroundColor: accent
    };
    $[5] = accent;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Send className="h-3 w-3" data-sentry-element="Send" data-sentry-source-file="goal-previews.tsx" />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6) {
    t8 = <div className="space-y-2">{t2}<motion.div initial={t3} animate={t4} transition={t5} className="ml-auto flex w-3/4 items-center gap-1.5 rounded-2xl rounded-br-sm px-3 py-2 text-xs text-white" style={t6} data-sentry-element="motion.div" data-sentry-source-file="goal-previews.tsx">{t7} Contract drafted and sent for signature</motion.div></div>;
    $[8] = t6;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== accent || $[11] !== tick) {
    t9 = <div className="mt-6 flex items-center justify-between">{stages.map((stage, i) => {
        const active = tick > i;
        return <div key={stage.label} className="flex flex-1 items-center"><div className="flex flex-col items-center gap-1"><motion.div className="flex h-9 w-9 items-center justify-center rounded-full" animate={{
              backgroundColor: active ? accent : "#f3f4f6",
              color: active ? "#ffffff" : "#9ca3af",
              scale: tick === i + 1 ? 1.12 : 1
            }}><stage.icon className="h-4 w-4" /></motion.div><span className="text-[10px] text-gray-500">{stage.label}</span></div>{i < stages.length - 1 ? <div className="mx-1 mb-4 h-0.5 flex-1 overflow-hidden rounded bg-gray-100"><motion.div className="h-full" style={{
              backgroundColor: accent
            }} animate={{
              width: tick > i + 1 ? "100%" : "0%"
            }} /></div> : null}</div>;
      })}</div>;
    $[10] = accent;
    $[11] = tick;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <PreviewWindow title="From hello to closed" data-sentry-element="PreviewWindow" data-sentry-component="ConversationToClosePreview" data-sentry-source-file="goal-previews.tsx">{t8}{t9}</PreviewWindow>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
export const GOAL_PREVIEWS: Record<GoalId, (props: GoalPreviewProps) => React.ReactElement> = {
  replace_legacy: ReplaceLegacyPreview,
  collect_esignatures: CollectSignaturesPreview,
  sign_documents: SignDocumentsPreview,
  web_forms: WebFormsPreview,
  automate_contracts: AutomateContractsPreview,
  contract_insights: ContractInsightsPreview,
  create_contracts: CreateContractsPreview,
  compliance: CompliancePreview,
  payments: PaymentsPreview,
  conversation_to_close: ConversationToClosePreview
};

/** Shown on the goals step before anything is picked. */
export function GoalsIdlePreview(t0) {
  const $ = _c(3);
  const {
    accent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [FileSignature, BarChart3, CreditCard, ShieldCheck, Sparkles, FileText];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== accent) {
    t2 = <div className="grid grid-cols-3 gap-3" data-sentry-component="GoalsIdlePreview" data-sentry-source-file="goal-previews.tsx">{t1.map((Icon, i) => <motion.div key={i} className="flex aspect-square items-center justify-center rounded-[25px] border border-gray-200 bg-white" animate={{
        y: [0, -6, 0]
      }} transition={{
        duration: 3,
        delay: i * 0.25,
        repeat: Number.POSITIVE_INFINITY,
        ease: "easeInOut"
      }}><Icon className="h-7 w-7" style={{
          color: accent
        }} /></motion.div>)}</div>;
    $[1] = accent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}